import { AUTH, FEATURES } from '../config/app-config';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import AuthTransitionPage from '../components/auth/AuthTransitionPage';
import { getOidcClient } from './oidc-client';

// Auth paths that indicate a login or logout
const AUTH_PATHS = new Set(['/auth/callback', '/auth/logged-out']);
const AuthContext = createContext(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
    return context;
};

function getSafeReturnPath(candidate, origin = window.location.origin) {
    try {
        const expectedOrigin = new URL(origin).origin;
        const target = new URL(candidate || '/', expectedOrigin);

        if (target.origin !== expectedOrigin || AUTH_PATHS.has(target.pathname)) {
            return '/';
        }

        return `${target.pathname}${target.search}${target.hash}`;
    } catch {
        return '/';
    }
}

export function AuthProvider({ children }) {
    const oidcClient = getOidcClient();
    const authEnabled = Boolean(AUTH?.enabled && oidcClient);
    const [user, setUser] = useState(null);
    const [isAdmin, setAdmin] = useState(false);
    const [isInitializing, setIsInitializing] = useState(authEnabled);
    const [initializationError, setInitializationError] = useState(null);
    const initializationInFlight = useRef(null);
    const reauthInFlight = useRef(null);

    const initUser = useCallback((nextUser) => {
        if (!nextUser) {
            setUser(null);
            setAdmin(false);
            return;
        }

        setUser(nextUser);
        const rolesClaim = nextUser.profile?.params?.Roles || '';
        const roles = String(rolesClaim)
            .split(';')
            .map((role) => role.trim())
            .filter(Boolean);
        setAdmin(FEATURES.admin && roles.includes(AUTH.adminRole));
    }, []);

    /*
     * Reauthenticates the user silently or redirects to login if necessary
     * @param {boolean} forceLogout If true, the user will be logged out completely
     */
    const reauthorize = useCallback(async ({ forceLogout = false } = {}) => {
        if (!authEnabled) throw new Error('Authentication is disabled for this OEM.');
        if (reauthInFlight.current) return reauthInFlight.current;

        reauthInFlight.current = (async () => {
            try {
                const renewedUser = await oidcClient.signinSilent();
                initUser(renewedUser);
                return renewedUser;
            } catch (error) {
                console.error('[OIDC] reauthorization failed', error);
                initUser(null);

                if (forceLogout) {
                    await oidcClient.signoutRedirect({ id_token_hint: user?.id_token });
                } else {
                    sessionStorage.setItem('originalUrl', window.location.href);
                    await oidcClient.signinRedirect({ state: { returnUrl: window.location.href } });
                }
                throw error;
            } finally {
                if (forceLogout) {
                    await oidcClient.removeUser();
                    sessionStorage.removeItem('originalUrl');
                }
                reauthInFlight.current = null;
            }
        })();

        return reauthInFlight.current;
    }, [authEnabled, initUser, oidcClient, user?.id_token]);

    useEffect(() => {
        if (!authEnabled) return;

        const urlHasAuthParams =
            /[?&](code|error)=/.test(window.location.search) ||
            /[#](code|error)=/.test(window.location.hash);
        const isSigninCallback =
            window.location.pathname === '/auth/callback' && urlHasAuthParams;
        const isLogoutCallback = window.location.pathname === '/auth/logged-out';

        const initialize = async () => {
            try {
                if (isSigninCallback) {
                    const nextUser = await oidcClient.signinRedirectCallback();
                    const returnPath = getSafeReturnPath(
                        nextUser.state?.returnUrl || sessionStorage.getItem('originalUrl') || '/'
                    );

                    // Rewrite the callback document before App mounts. This preserves
                    // the loaded SPA while ensuring every App hook sees the final URL.
                    window.history.replaceState(window.history.state, '', returnPath);
                    sessionStorage.removeItem('originalUrl');
                    initUser(nextUser);
                    return;
                }

                if (isLogoutCallback) {
                    await oidcClient.signoutRedirectCallback();
                    initUser(null);
                    sessionStorage.removeItem('originalUrl');
                    return;
                }

                let currentUser = await oidcClient.getUser();
                if (currentUser?.expired) {
                    currentUser = await reauthorize({ forceLogout: true });
                }
                initUser(currentUser);
            } catch (error) {
                const message = isSigninCallback
                    ? '[OIDC] signinRedirectCallback failed'
                    : isLogoutCallback
                        ? '[OIDC] signoutRedirectCallback failed'
                        : '[OIDC] initial auth load failed';
                console.error(message, error);

                if (isSigninCallback) {
                    setInitializationError(error);
                } else {
                    initUser(null);
                }
            } finally {
                setIsInitializing(false);
            }
        };

        // React StrictMode replays effects in development. Reuse the same promise
        // so the one-time OIDC state cannot be consumed twice.
        if (!initializationInFlight.current) {
            initializationInFlight.current = initialize();
        }
    }, [authEnabled, initUser, oidcClient, reauthorize]);

    // Cross-tab auth sync + active token renewal
    useEffect(() => {
        if (!authEnabled) return undefined;

        const handleLoad = (nextUser) => initUser(nextUser);
        const handleUnload = () => initUser(null);
        const handleExpiry = () => {
            reauthorize({ forceLogout: true }).catch(() => {});
        };

        oidcClient.events.addUserLoaded(handleLoad);
        oidcClient.events.addUserUnloaded(handleUnload);
        oidcClient.events.addAccessTokenExpiring(handleExpiry);
        oidcClient.events.addAccessTokenExpired(handleExpiry);

        return () => {
            oidcClient.events.removeUserLoaded(handleLoad);
            oidcClient.events.removeUserUnloaded(handleUnload);
            oidcClient.events.removeAccessTokenExpiring(handleExpiry);
            oidcClient.events.removeAccessTokenExpired(handleExpiry);
        };
    }, [authEnabled, initUser, oidcClient, reauthorize]);

    // API
    const value = useMemo(() => ({
        enabled: authEnabled,
        user,
        isAuthenticated: authEnabled && Boolean(user && !user.expired),
        isAdmin: authEnabled && isAdmin,
        login: () => {
            if (!authEnabled) return Promise.resolve();
            sessionStorage.setItem('originalUrl', window.location.href);
            return oidcClient.signinRedirect({ state: { returnUrl: window.location.href } });
        },
        logout: () => authEnabled ? oidcClient.signoutRedirect() : Promise.resolve(),
    }), [authEnabled, isAdmin, oidcClient, user]);

    const isSigninCallback = window.location.pathname === '/auth/callback';
    const content = (isInitializing || (isSigninCallback && initializationError))
        ? <AuthTransitionPage error={initializationError} />
        : children;

    return <AuthContext.Provider value={value}>{content}</AuthContext.Provider>;
}
